<template>
  <Modal
    :title="titleText"
    :title-center="false"
    medium
    :is-loading="isLoading"
  >
    <div class="flex flex-col items-start w-full gap-5">
      <div
        class="w-full mb-4 flex flex-col gap-5"
        v-if="isExistingLoan"
      >
        <h1 class="text-xl font-bold text-white">
          #{{ tokenId }}
          {{ selectedCollateralInfo.token.symbol }} collateralized
          loan
        </h1>
        <Block type="info" class="w-full">
          <TextInfo
            :img-value="selectedCollateralInfo.token.img"
            :title="`${selectedCollateralInfo.token.symbol} Deposited:`"
            :value="`${amountCurrentlyDepositedFormatted} ${selectedCollateralInfo.token.symbol} ($${amountCurrentlyDepositedUSDFormatted})`"
          />
          <TextInfo
            img-value="/img/eth.png"
            :title="`${nativeCurrencySymbol} Borrowed:`"
            :value="`${amountCurrentlyBorrowedFormatted} ${nativeCurrencySymbol} ($${amountCurrentlyBorrowedUSDFormatted})`"
          />
        </Block>
        <Block type="info" class="w-full">
          <TextInfo
            img="/icon/hand-coin.png"
            title="Current LTV:"
            :value="`${ltvPercCurrent}%`"
            :danger="!isLoanHealthy"
            :success="isLoanHealthy"
          />
          <TextInfo
            img="/icon/hand-coin.png"
            title="Fees Accumulated:"
            :value="`${aprFeesAccumulatedFormatted} ${nativeCurrencySymbol}`"
            :danger="!isLoanHealthy"
            :success="isLoanHealthy"
          />
        </Block>
      </div>
      <CollateralSelect
        v-else
        label="Collateral"
        v-model="selectedCollateralInfo"
      />
      <Input
        v-if="!borrowOnly && !payOnly && !withdrawOnly"
        :label="`${selectedCollateralSymbol} to Deposit ($${depositAmountUSDFormatted})`"
        show-balance
        v-model="depositAmountNoDecimals"
        :max="selectedCollateralBalanceFixed"
        :balance="selectedCollateralBalanceFixed"
        :icon="selectedCollateralInfo.token.img"
      />
      <Input
        v-if="!depositOnly && !payOnly && !withdrawOnly"
        :label="`${nativeCurrencySymbol} to Borrow ($${borrowAmountUSDFormatted})`"
        show-balance
        :balance-title="`Max ${nativeCurrencySymbol} can borrow`"
        :balance="maxETHCanBorrow"
        v-model="borrowAmountNoDecimals"
        :max="maxETHCanBorrow"
      />
      <Input
        v-if="payOnly"
        :label="`${nativeCurrencySymbol} to Pay ($${payAmountUSDFormatted})`"
        v-model="payAmountNoDecimals"
        show-balance
        :balance-title="`Max to pay back`"
        :balance="maxETHCanPayBack"
        :max="maxETHCanPayBack"
      />
      <Input
        v-if="withdrawOnly"
        :label="`${selectedCollateralSymbol} to Withdraw ($${withdrawAmountUSDFormatted})`"
        v-model="withdrawAmountNoDecimals"
        show-balance
        :balance-title="`Max to withdraw`"
        :balance="maxAmountCanWithdrawFixed"
        :max="maxAmountCanWithdrawFixed"
      />
      <Block type="info" class="w-full">
        <TextInfo
          img="/icon/coin.png"
          :title="`Open Fee (${borrowOpenFeePerc}%):`"
          :value="`${borrowOpenFeeFormatted} ${nativeCurrencySymbol} ($${borrowOpenFeeUSDFormatted})`"
        />
        <TextInfo
          img="/icon/coin.png"
          title="Weekly Borrow Fees:"
          :value="`${estWeeklyFeesETHFormatted} ${nativeCurrencySymbol} ($${estWeeklyFeesUSDFormatted})`"
        />
        <TextInfo
          img="/icon/coin.png"
          title="New LTV:"
          :value="`${ltvPercNew}%`"
        />
      </Block>
      <div
        class="flex flex-wrap items-stretch gap-5 w-full border-t mt-2 pt-7 border-white border-opacity-10"
      >
        <LoanInfo
          icon="/svg/money.svg"
          :value="`${maxLTVPercentFixed}%`"
          description="Max LTV"
        />
        <LoanInfo
          icon="/svg/percentage.svg"
          :value="`${currentAPRFormatted}%`"
          description="Current Borrow APR"
          class="flex-1"
        />
      </div>
      <div
        class="flex items-center justify-between w-full gap-5 mt-5"
      >
        <Button
          :text="btnText"
          secondary
          large
          class="w-full"
          @click="process"
          :disabled="!isOnValidNetwork || !isButtonEnabled"
          :is-loading="isLoading"
        />
      </div>
    </div>
    <Loading v-if="isLoading" />
  </Modal>
</template>

<script>
import BigNumber from "bignumber.js";
import dayjs from "dayjs";
import { mapActions, mapGetters, mapState } from "vuex";
import Modal from "../global/Modal.vue";
import CollateralSelect from "../collateral/CollateralSelect.vue";
import Input from "../global/Input.vue";
import Button from "../global/Button.vue";
import Loading from "../global/Loading.vue";
import LoanInfo from "./LoanInfo.vue";
import Block from "../global/Block.vue";
import TextInfo from "../global/TextInfo.vue";
import { calculateAPRFees } from "../../factories/LoanUtils";
import { formatDynamicDecimals } from "../../factories/Utils";
export default {
  name: "LoanModal",
  components: {
    Modal,
    CollateralSelect,
    Input,
    Button,
    Loading,
    LoanInfo,
    Block,
    TextInfo,
  },

  props: {
    lendingContract: { type: String },
    tokenId: { type: [Number, String], default: 0 },
    alreadyBorrowAmount: { type: [Number, String], default: 0 },
    alreadyDepositAmount: { type: [Number, String], default: 0 },
    borrowOnly: { type: Boolean, default: false },
    depositOnly: { type: Boolean, default: false },
    payOnly: { type: Boolean, default: false },
    withdrawOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      isLoading: false,
      depositAmountNoDecimals: 0,
      borrowAmountNoDecimals: 0,
      payAmountNoDecimals: 0,
      withdrawAmountNoDecimals: 0,
    };
  },

  computed: {
    ...mapState({
      denomenator: ({ main }) => main.denomenator,
      mainPoolEnabled: ({ main }) =>
        main.lendingPoolData && main.lendingPoolData.enabled,
      extPoolEnabled: ({ main }) =>
        main.lendingPoolExtData && main.lendingPoolExtData.enabled,
      borrowAPRRaw: ({ main }) =>
        (main.lendingPoolData &&
          main.lendingPoolData.calculateBorrowAPR) ||
        0,
      borrowOpenFee: ({ main }) =>
        (main.lendingPoolData &&
          main.lendingPoolData.borrowInitFee) ||
        0,
      currentAPR: ({ main }) =>
        main.lendingPoolData &&
        main.lendingPoolData.calculateBorrowAPR,
      maxLTVRaw: ({ main }) =>
        main.lendingPoolData && main.lendingPoolData.maxLTVOverall,
      selectedPool: ({ main }) => main.selectedCollateralPool,
      ltvHealthThreshold: ({ main }) => main.ltvHealthThreshold,
      ethInMainLendingPool: ({ main }) =>
        (main.lendingPoolETHBal && main.lendingPoolETHBal.balance) ||
        0,
      ethInExtLpLendingPool: ({ main }) =>
        (main.lendingPoolExtETHBal &&
          main.lendingPoolExtETHBal.balance) ||
        0,
      maxLTVPoolOverride({ main }) {
        return (
          (main.collPoolMaxInfo &&
            this.selectedCollateralInfo &&
            this.selectedCollateralInfo.pool &&
            main.collPoolMaxInfo[
              `maxLTVOverride_${this.selectedCollateralInfo.pool.toLowerCase()}`
            ]) ||
          "0"
        );
      },

      currentBorrowForPool({ main }) {
        return (
          (this.selectedCollateralInfo &&
            main.collPoolExtMaxInfo &&
            main.collPoolExtMaxInfo[
              `currentBorrowPerPool_${this.selectedCollateralInfo.pool.toLowerCase()}`
            ]) ||
          0
        );
      },

      maxBorrowForPool({ main }) {
        return (
          (this.selectedCollateralInfo &&
            main.collPoolExtMaxInfo &&
            main.collPoolExtMaxInfo[
              `maxBorrowPerPool_${this.selectedCollateralInfo.pool.toLowerCase()}`
            ]) ||
          0
        );
      },

      tokenLoanInfo({ main }) {
        if (!(main.loanSecondaryInfo || main.loanSecondaryExtInfo)) {
          return;
        }
        return this.lendingContract &&
          this.activeNetwork &&
          this.lendingContract.toLowerCase() ===
            this.activeNetwork.contracts.lendingPoolExt.toLowerCase()
          ? main.loanSecondaryExtInfo[`loan_${this.tokenId}`]
          : main.loanSecondaryInfo[`loan_${this.tokenId}`];
      },

      tokenLTVInfo({ main }) {
        return (
          (main.loanSecondaryInfo &&
            main.loanSecondaryInfo[`ltvX96_${this.tokenId}`]) ||
          (main.loanSecondaryExtInfo &&
            main.loanSecondaryExtInfo[`ltvX96_${this.tokenId}`])
        );
      },

      userCollateralBalance({ main }) {
        const balObj =
          main[
            `tokenUserBal_${this.selectedCollateralInfo.token.address.toLowerCase()}`
          ];
        return (balObj && balObj.balanceOf) || 0;
      },
    }),
    ...mapGetters([
      "activeNetwork",
      "collateralPools",
      "ethPriceUSDFixed",
      "isOnValidNetwork",
      "loanCollateralTokens",
      "nativeCurrencySymbol",
      "userETHBalanceFixed",
      "userETHBalanceFormatted",
    ]),

    poolEnabled() {
      return this.lendingContract &&
        this.activeNetwork &&
        this.lendingContract.toLowerCase() ===
          this.activeNetwork.contracts.lendingPool.toLowerCase()
        ? this.mainPoolEnabled
        : this.extPoolEnabled;
    },

    loan() {
      if (!this.tokenLoanInfo) {
        return {};
      }
      return {
        created: this.tokenLoanInfo[0],
        createdBy: this.tokenLoanInfo[1],
        aprStart: this.tokenLoanInfo[2],
        collateralPool: this.tokenLoanInfo[3],
        amountDeposited: this.tokenLoanInfo[4],
        amountETHBorrowed: this.tokenLoanInfo[5],
      };
    },

    isExistingLoan() {
      return this.tokenId && new BigNumber(this.tokenId).gt(0);
    },

    isLoanHealthy() {
      return this.ltvPercCurrent
        ? new BigNumber(this.ltvPercCurrent).lte(
            this.ltvHealthThreshold
          )
        : true;
    },

    maxAvailableToBorrowFromPoolLimit() {
      const max = new BigNumber(this.maxBorrowForPool);
      if (max.lte(0)) {
        return this.maxBorrowFromLendingPool;
      }
      const diff = max.minus(this.currentBorrowForPool);
      return diff.lt(0) ? "0" : diff.toFixed();
    },

    maxBorrowFromLendingPool() {
      if (
        this.lendingContract &&
        new BigNumber(this.lendingContract.toLowerCase()).gt(0)
      ) {
        return this.lendingContract.toLowerCase() ===
          this.activeNetwork.contracts.lendingPool.toLowerCase()
          ? this.ethInMainLendingPool
          : this.ethInExtLpLendingPool;
      }
      return new BigNumber(this.ethInMainLendingPool).gt(
        this.ethInExtLpLendingPool
      )
        ? this.ethInMainLendingPool
        : this.ethInExtLpLendingPool || 0;
    },

    amountCurrentlyDepositedFixed() {
      return new BigNumber(this.loan.amountDeposited || 0)
        .div(
          new BigNumber(10).pow(
            this.selectedCollateralInfo.token.decimals
          )
        )
        .toFixed();
    },

    amountCurrentlyDepositedFormatted() {
      return formatDynamicDecimals(
        new BigNumber(this.amountCurrentlyDepositedFixed)
      );
    },

    amountCurrentlyDepositedUSDFormatted() {
      return new BigNumber(this.amountCurrentlyDepositedFixed)
        .times(this.selectedCollateralInfo.priceUSD)
        .toFormat(2);
    },

    amountCurrentlyBorrowedFixed() {
      return new BigNumber(this.loan.amountETHBorrowed || 0)
        .div(new BigNumber(10).pow(18))
        .toFixed();
    },

    amountCurrentlyBorrowedFormatted() {
      return formatDynamicDecimals(
        new BigNumber(this.amountCurrentlyBorrowedFixed)
      );
    },

    amountCurrentlyBorrowedUSDFormatted() {
      return new BigNumber(this.amountCurrentlyBorrowedFixed)
        .times(this.ethPriceUSDFixed)
        .toFormat(2);
    },

    btnText() {
      if (!this.poolEnabled && !(this.payOnly || this.withdrawOnly)) {
        return `Disabled`;
      }
      if (!this.isOnValidNetwork) {
        return `Invalid Network`;
      }
      if (this.depositOnly) {
        return `Deposit ${this.selectedCollateralInfo.token.symbol}`;
      }
      if (this.borrowOnly) {
        return `Borrow ${this.nativeCurrencySymbol}`;
      }
      if (this.payOnly) {
        return `Pay Back Loan`;
      }
      if (this.withdrawOnly) {
        return "Withdraw Collateral";
      }
      return "Create Loan";
    },

    titleText() {
      if (this.depositOnly) {
        return `Deposit Collateral`;
      }
      if (this.borrowOnly) {
        return `Borrow`;
      }
      if (this.payOnly) {
        return `Pay Back Loan`;
      }
      if (this.withdrawOnly) {
        return `Withdraw Collateral`;
      }
      return "Loan";
    },

    isButtonEnabled() {
      if (!this.poolEnabled) {
        return this.payOnly || this.withdrawOnly ? true : false;
      }
      if (this.borrowOnly || this.payOnly || this.withdrawOnly) {
        return (
          this.loan.collateralPool &&
          !!this.collateralPools.find(
            (p) =>
              p.address.toLowerCase() ===
              this.loan.collateralPool.toLowerCase()
          )
        );
      }
      return true;
    },

    borrowOpenFeePerc() {
      return formatDynamicDecimals(
        new BigNumber(this.borrowOpenFee)
          .div(this.denomenator)
          .times(100)
      );
    },

    borrowOpenFeeFixed() {
      const openFee = this.borrowOpenFee;
      const borrowAmount = new BigNumber(
        this.borrowAmountNoDecimals || 0
      );
      if (!borrowAmount || borrowAmount.isNaN()) {
        return "0";
      }
      return borrowAmount
        .times(openFee)
        .div(this.denomenator)
        .toFixed();
    },

    borrowOpenFeeFormatted() {
      return formatDynamicDecimals(this.borrowOpenFeeFixed);
    },

    borrowOpenFeeUSDFormatted() {
      return new BigNumber(this.borrowOpenFeeFixed)
        .times(this.ethPriceUSDFixed)
        .toFormat(2);
    },

    maxAmountCanWithdrawFixed() {
      const maxLTV100 = new BigNumber(this.maxLTVPercentFixed);
      const currentBorrowedUSD = new BigNumber(
        this.amountCurrentlyBorrowedFixed
      ).times(this.ethPriceUSDFixed);
      const currentDepositedUSD = new BigNumber(
        this.amountCurrentlyDepositedFixed
      ).times(this.selectedCollateralInfo.priceUSD);
      const minCanHaveDepositedUSD = currentBorrowedUSD.div(
        maxLTV100.div(100)
      );
      if (currentDepositedUSD.lt(minCanHaveDepositedUSD)) {
        return "0";
      }
      return currentDepositedUSD
        .minus(minCanHaveDepositedUSD)
        .div(this.selectedCollateralInfo.priceUSD)
        .times("0.998")
        .toFixed();
    },

    aprFeesAccumulatedWei() {
      return calculateAPRFees(
        this.borrowAPRRaw,
        this.loan.amountETHBorrowed || 0,
        this.loan.aprStart || dayjs().unix()
      );
    },

    aprFeesAccumulatedFormatted() {
      const amountFeesETHNoDecimals = new BigNumber(
        this.aprFeesAccumulatedWei
      );
      return formatDynamicDecimals(
        amountFeesETHNoDecimals.div(new BigNumber(10).pow(18))
      );
    },

    ltvPercCurrent() {
      const ltv =
        (this.tokenLTVInfo &&
          this.tokenLTVInfo[0] &&
          new BigNumber(this.tokenLTVInfo[0])
            .div(new BigNumber(2).pow(96))
            .toFixed()) ||
        0;
      return new BigNumber(ltv).times(100).toNumber().toFixed(2) || 0;
    },

    ltvPercNew() {
      if (
        this.depositAmountNoDecimals == "NaN" ||
        this.borrowAmountNoDecimals == "NaN" ||
        this.payAmountNoDecimals == "NaN" ||
        this.withdrawAmountNoDecimals == "NaN"
      ) {
        return this.ltvPercCurrent;
      }
      const currentBorrowedAmountUSD = new BigNumber(
        this.loan.amountETHBorrowed || 0
      )
        .div(new BigNumber(10).pow(18))
        .times(this.ethPriceUSDFixed);
      const newBorrowedAmountUSD = new BigNumber(
        this.borrowAmountNoDecimals || 0
      ).times(this.ethPriceUSDFixed);
      const payBackAmountUSD = new BigNumber(
        this.payAmountNoDecimals || 0
      ).times(this.ethPriceUSDFixed);
      const currentDepositAmountUSD = new BigNumber(
        this.loan.amountDeposited || 0
      )
        .div(
          new BigNumber(10).pow(
            this.selectedCollateralInfo.token.decimals
          )
        )
        .times(this.selectedCollateralInfo.priceUSD);
      const newDepositAmountUSD = new BigNumber(
        this.depositAmountNoDecimals || 0
      ).times(this.selectedCollateralInfo.priceUSD);
      const newWithdrawAmountUSD = new BigNumber(
        this.withdrawAmountNoDecimals || 0
      ).times(this.selectedCollateralInfo.priceUSD);
      if (
        new BigNumber(currentDepositAmountUSD)
          .plus(newDepositAmountUSD)
          .minus(newWithdrawAmountUSD)
          .eq(0)
      ) {
        return this.ltvPercCurrent;
      }
      return new BigNumber(currentBorrowedAmountUSD)
        .plus(newBorrowedAmountUSD)
        .minus(payBackAmountUSD)
        .div(
          new BigNumber(currentDepositAmountUSD)
            .plus(newDepositAmountUSD)
            .minus(newWithdrawAmountUSD)
        )
        .times(100)
        .toNumber()
        .toFixed(2);
    },

    estWeeklyFeesETHFixed() {
      if (
        this.depositAmountNoDecimals == "NaN" ||
        this.borrowAmountNoDecimals == "NaN" ||
        this.payAmountNoDecimals == "NaN"
      ) {
        return "0.00";
      }
      const borrowAprDecimal = new BigNumber(
        this.currentAPRFixed
      ).div(100);
      const currentBorrowedAmountUSD = new BigNumber(
        this.loan.amountETHBorrowed || 0
      ).div(new BigNumber(10).pow(18));
      const newBorrowedAmountUSD = new BigNumber(
        this.borrowAmountNoDecimals || 0
      );
      const payBackAmountUSD = new BigNumber(
        this.payAmountNoDecimals || 0
      );
      return currentBorrowedAmountUSD
        .plus(newBorrowedAmountUSD)
        .minus(payBackAmountUSD)
        .times(borrowAprDecimal)
        .div(52)
        .toFixed();
    },

    estWeeklyFeesETHFormatted() {
      return formatDynamicDecimals(this.estWeeklyFeesETHFixed);
    },

    estWeeklyFeesUSDFormatted() {
      return new BigNumber(this.estWeeklyFeesETHFixed)
        .times(this.ethPriceUSDFixed)
        .toFixed(2);
    },

    depositAmountUSDFormatted() {
      if (this.depositAmountNoDecimals == "NaN") {
        return "0.00";
      }
      const collateralPriceUSD = this.selectedCollateralInfo.priceUSD;
      return new BigNumber(this.depositAmountNoDecimals || 0)
        .times(collateralPriceUSD)
        .toFormat(2);
    },

    withdrawAmountUSDFormatted() {
      if (this.withdrawAmountNoDecimals == "NaN") {
        return "0.00";
      }
      const collateralPriceUSD = this.selectedCollateralInfo.priceUSD;
      return new BigNumber(this.withdrawAmountNoDecimals || 0)
        .times(collateralPriceUSD)
        .toFormat(2);
    },

    borrowAmountUSDFormatted() {
      const ethPriceUSD = this.ethPriceUSDFixed;
      const borrowAmount = new BigNumber(this.borrowAmountNoDecimals);
      if (!borrowAmount || borrowAmount.isNaN()) {
        return "0.00";
      }
      return borrowAmount.times(ethPriceUSD).toFormat(2);
    },

    payAmountUSDFormatted() {
      if (!this.ethPriceUSDFixed) {
        return "0.00";
      }
      const ethPriceUSD = this.ethPriceUSDFixed;
      return new BigNumber(this.payAmountNoDecimals)
        .times(ethPriceUSD)
        .toFormat(2);
    },

    maxLTVPercentFixed() {
      const ltv = new BigNumber(this.maxLTVPoolOverride).gt(0)
        ? this.maxLTVPoolOverride
        : this.maxLTVRaw || 0;
      return new BigNumber(ltv)
        .div(this.denomenator)
        .times(100)
        .toFixed();
    },

    maxETHCanPayBack() {
      const ethBal = this.userETHBalanceFixed;
      const ethBorrowed = this.amountCurrentlyBorrowedFixed;
      return new BigNumber(ethBal).gt(ethBorrowed)
        ? ethBorrowed
        : ethBal;
    },

    selectedCollateralSymbol() {
      return (
        (this.selectedCollateralInfo &&
          this.selectedCollateralInfo.token &&
          this.selectedCollateralInfo.token.symbol) ||
        "N/A"
      );
    },

    selectedCollateralInfo: {
      get() {
        return this.loanCollateralTokens.find((t) => {
          if (this.isExistingLoan) {
            return (
              (this.loan.collateralPool || "").toLowerCase() ==
              t.pool.toLowerCase()
            );
          }
          return (
            t.pool.toLowerCase() === this.selectedPool.toLowerCase()
          );
        });
      },
      set(newVal) {
        this.$store.commit("SET_SELECTED_COLLATERAL_POOL", newVal);
      },
    },

    currentAPRFixed() {
      return new BigNumber(this.currentAPR || 0)
        .div(this.denomenator)
        .times(100)
        .toFixed();
    },

    currentAPRFormatted() {
      return new BigNumber(this.currentAPRFixed).toFormat(2);
    },

    selectedCollateralBalanceFixed() {
      return (
        (this.selectedCollateralInfo &&
          new BigNumber(this.userCollateralBalance)
            .div(
              new BigNumber(10).pow(
                this.selectedCollateralInfo.token.decimals
              )
            )
            .toFixed()) ||
        "0"
      );
    },

    // Get max user can borrow from deposited collateral, and take
    // the lesser of that and the ETH in the lending pool
    maxETHCanBorrow() {
      const poolETHNoDecimals = new BigNumber(
        this.maxBorrowFromLendingPool
      ).div(new BigNumber(10).pow(18));
      const maxAvailablePerLimit = new BigNumber(
        this.maxAvailableToBorrowFromPoolLimit
      ).div(new BigNumber(10).pow(18));
      const ethPriceUSD = this.ethPriceUSDFixed;
      const alreadyDeposited = this.alreadyDepositAmount;
      const collateralDepositAmount = new BigNumber(
        alreadyDeposited
      ).gt(this.depositAmountNoDecimals)
        ? alreadyDeposited
        : this.depositAmountNoDecimals;
      const collateralPriceUSD = this.selectedCollateralInfo.priceUSD;
      const collateralDepositAmountUSD = new BigNumber(
        collateralDepositAmount
      ).times(collateralPriceUSD);
      const depositAmountETH =
        collateralDepositAmountUSD.div(ethPriceUSD);
      const maxBorrowAmountETHFromDeposit = depositAmountETH
        .times(this.maxLTVPercentFixed)
        .div(100);
      const maxBorrowFromDep = maxBorrowAmountETHFromDeposit.minus(
        this.alreadyBorrowAmount
      );
      const maxBorrowGtZero = maxBorrowFromDep.lt(0)
        ? new BigNumber(0)
        : maxBorrowFromDep;
      const maxBorrowBetweenDepositAndPool = maxBorrowFromDep.lt(
        poolETHNoDecimals
      )
        ? maxBorrowGtZero
        : poolETHNoDecimals;
      return maxBorrowBetweenDepositAndPool.lt(maxAvailablePerLimit)
        ? maxBorrowBetweenDepositAndPool.toFixed()
        : maxAvailablePerLimit.toFixed();
    },
  },

  methods: {
    ...mapActions(["depositAndBorrow", "payBackLoan", "withdraw"]),

    async process() {
      if (this.payOnly) {
        await this.payBackOnLoan();
      } else if (this.withdrawOnly) {
        await this.withdrawFromLoan();
      } else {
        await this.createLoan();
      }
    },

    async payBackOnLoan() {
      try {
        this.isLoading = true;

        const payBack = new BigNumber(this.payAmountNoDecimals).times(
          new BigNumber(10).pow(18)
        );
        if (payBack.lte(0)) {
          return this.$notify({
            type: "error",
            text: `Please enter a valid amount of ETH to pay back on the loan.`,
          });
        }

        await this.payBackLoan({
          lendingContract: this.lendingContract,
          tokenId: this.tokenId,
          feesAccumulatedWei: this.aprFeesAccumulatedWei,
          value: payBack.toFixed(0),
        });
        this.$notify({
          type: "success",
          text: `Successully paid back on your loan!`,
        });
        this.$emit("close");
      } catch (err) {
        this.$notify({
          type: "error",
          text: err.message,
        });
        console.error(`Error paying back loan`, err);
      } finally {
        this.isLoading = false;
      }
    },

    async withdrawFromLoan() {
      try {
        this.isLoading = true;

        const withdrawAmount = new BigNumber(
          this.withdrawAmountNoDecimals
        ).times(
          new BigNumber(10).pow(
            this.selectedCollateralInfo.token.decimals
          )
        );
        if (withdrawAmount.eq(0)) {
          return this.$notify({
            type: "error",
            text: `Please specify an amount to withdraw.`,
          });
        }
        if (
          new BigNumber(this.withdrawAmountNoDecimals).gt(
            this.maxAmountCanWithdrawFixed
          )
        ) {
          return this.$notify({
            type: "error",
            text: `You cannot withdraw more than would make your LTV greater than ${this.maxLTVPercentFixed}%`,
          });
        }
        await this.withdraw({
          lendingContract: this.lendingContract,
          tokenId: this.tokenId,
          amount: withdrawAmount.toFixed(0),
        });
      } catch (err) {
        this.$notify({
          type: "error",
          text: err.message,
        });
        console.error(`Error withdrawing from loan`, err);
      } finally {
        this.isLoading = false;
      }
    },

    async createLoan() {
      try {
        this.isLoading = true;

        const depositAmount = new BigNumber(
          this.depositAmountNoDecimals
        ).times(
          new BigNumber(10).pow(
            this.selectedCollateralInfo.token.decimals
          )
        );
        let borrowAmount = new BigNumber(
          this.borrowAmountNoDecimals
        ).times(new BigNumber(10).pow(18));
        if (depositAmount.eq(0) && borrowAmount.eq(0)) {
          return this.$notify({
            type: "error",
            text: `Please either add an amount of collateral to deposit or ETH to borrow.`,
          });
        }

        // cannot deposit more than your balance
        if (
          new BigNumber(this.depositAmountNoDecimals).gt(
            this.selectedCollateralBalanceFixed
          )
        ) {
          return this.$notify({
            type: "error",
            text: `Please enter a valid deposit amount up to but not greater than your overall wallet balance.`,
          });
        }

        // trying to borrow but cannot borrow anymore funds
        if (
          borrowAmount.gt(0) &&
          new BigNumber(this.maxETHCanBorrow).lte(0)
        ) {
          return this.$notify({
            type: "error",
            text: `This loan's LTV is maxed out. Please pay back on the loan to be able to borrow against it later again.`,
          });
        }

        // validate borrow amount and if trying for max, reduce pad by 98.5%
        if (
          new BigNumber(this.maxETHCanBorrow)
            .minus(borrowAmount)
            .abs()
            .div(this.maxETHCanBorrow)
            .lte("0.02")
        ) {
          borrowAmount = new BigNumber(borrowAmount).times("0.985");
        }

        await this.depositAndBorrow({
          lendingContract: this.lendingContract,
          tokenId: this.tokenId,
          collateralToken: this.selectedCollateralInfo.token.address,
          pool: this.selectedCollateralInfo.pool,
          depositAmount: depositAmount.toFixed(0),
          borrowAmount: borrowAmount.toFixed(0),
        });
        this.$notify({
          type: "success",
          text: `Successully updated loan!`,
        });
        this.$emit("close");
      } catch (err) {
        this.$notify({
          type: "error",
          text: err.message,
        });
        console.error(`Error updating loan`, err);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
